import RequestUtils from "utils/RequestUtils";

export type MessageAudience = "customer" | "cast" | "staff" | "driver";

export interface MessageTemplateRes {
  messageTemplateId: number;
  companyId: number;
  shopId: number | null;
  audience: MessageAudience;
  name: string;
  subject: string | null;
  body: string;
  // visit Phase 3 (5/27+): URL 短縮 SMS テンプレ (starter/premium 専用)
  // null = 通常 SMS、 値あり = SMS 本文の {URL_PAGE} を短縮 URL に置換し、 この本文を URL 先ページで表示
  urlPageBody: string | null;
  urlPageTitle: string | null;
  enabledFields: string[] | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMessageTemplateReq {
  audience: MessageAudience;
  shopId?: number | null;
  name: string;
  subject?: string | null;
  body: string;
  urlPageBody?: string | null;
  urlPageTitle?: string | null;
  enabledFields?: string[] | null;
  isDefault?: boolean;
}

export interface UpdateMessageTemplateReq {
  shopId?: number | null;
  name?: string;
  subject?: string | null;
  body?: string;
  urlPageBody?: string | null;
  urlPageTitle?: string | null;
  enabledFields?: string[] | null;
  isDefault?: boolean;
}

// URL 短縮機能を利用可能なプラン
export const URL_PAGE_ALLOWED_PLANS = ["starter", "premium", "enterprise"];

export function isPlanAllowingUrlPage(
  plan: string | null | undefined
): boolean {
  return !!plan && URL_PAGE_ALLOWED_PLANS.includes(plan);
}

export default class MessageTemplateApi {
  private constructor() {}

  public static async list(
    companyId: number,
    audience?: MessageAudience
  ): Promise<MessageTemplateRes[]> {
    const qs = audience ? `?audience=${audience}` : "";
    return RequestUtils.getPlain(
      `/messageTemplate/list/${companyId}${qs}`
    ) as Promise<MessageTemplateRes[]>;
  }

  public static async find(
    companyId: number,
    templateId: number
  ): Promise<MessageTemplateRes> {
    return RequestUtils.getPlain(
      `/messageTemplate/find/${companyId}/${templateId}`
    ) as Promise<MessageTemplateRes>;
  }

  public static async create(
    companyId: number,
    data: CreateMessageTemplateReq
  ): Promise<MessageTemplateRes> {
    return RequestUtils.postPlain(
      `/messageTemplate/create/${companyId}`,
      data
    ) as Promise<MessageTemplateRes>;
  }

  public static async update(
    companyId: number,
    templateId: number,
    data: UpdateMessageTemplateReq
  ): Promise<MessageTemplateRes> {
    return RequestUtils.postPlain(
      `/messageTemplate/update/${companyId}/${templateId}`,
      data
    ) as Promise<MessageTemplateRes>;
  }

  public static async delete(
    companyId: number,
    templateId: number
  ): Promise<{ ok: boolean }> {
    return RequestUtils.postPlain(
      `/messageTemplate/delete/${companyId}/${templateId}`,
      {}
    ) as Promise<{ ok: boolean }>;
  }
}

/** プレースホルダ / enabled_fields 候補。 カテゴリ別に整理 (visit + delivery 互換) */
export interface FieldDef {
  key: string;
  label: string;
}
export interface FieldCategory {
  label: string;
  fields: FieldDef[];
}

export const FIELD_CATEGORIES: FieldCategory[] = [
  {
    label: "顧客",
    fields: [
      { key: "guestName", label: "顧客名" },
      { key: "guestKana", label: "顧客カナ" },
      { key: "guestTel", label: "顧客電話番号" },
      { key: "guestCategory", label: "顧客区分" },
      { key: "guestMemo", label: "顧客メモ" },
    ],
  },
  {
    label: "店舗 / キャスト",
    fields: [
      { key: "shopName", label: "店舗名" },
      { key: "castName", label: "キャスト名" },
      { key: "castClassName", label: "クラス名" },
    ],
  },
  {
    label: "予約日時",
    fields: [
      { key: "orderDate", label: "受付日時" },
      { key: "orderPlanInTime", label: "予定 IN 時間" },
      { key: "orderPlanOutTime", label: "予定 OUT 時間" },
      { key: "orderActualInTime", label: "実 IN 時間" },
      { key: "orderActualEndTime", label: "実 OUT 時間" },
      { key: "cleaningEndTime", label: "清掃終了予定" },
      { key: "roomName", label: "ルーム名" },
    ],
  },
  {
    label: "コース / 料金",
    fields: [
      { key: "courseName", label: "コース名" },
      { key: "totalTime", label: "合計時間 (分)" },
      { key: "additionalTime", label: "延長 (分)" },
      { key: "totalFee", label: "合計金額" },
      { key: "paymentType", label: "支払い方法" },
    ],
  },
  {
    label: "オプション / 割引",
    fields: [
      { key: "optionName", label: "オプション" },
      { key: "discountName", label: "イベント・割引" },
    ],
  },
  {
    label: "場所 (delivery)",
    fields: [
      { key: "areaName", label: "エリア" },
      { key: "hotelName", label: "ホテル" },
      { key: "orderAddress", label: "住所" },
    ],
  },
  {
    label: "メモ / 受付者",
    fields: [
      { key: "orderMemo", label: "受注メモ" },
      { key: "orderCreatedBy", label: "受付作成者" },
      { key: "orderUpdatedBy", label: "受付最終更新者" },
    ],
  },
];

/** 旧 API 互換 (フラット一覧) */
export const AVAILABLE_FIELDS: FieldDef[] = FIELD_CATEGORIES.flatMap(
  (c) => c.fields
);

export const AUDIENCE_LABELS: Record<MessageAudience, string> = {
  customer: "顧客向け",
  cast: "キャスト向け",
  staff: "スタッフ向け",
  driver: "ドライバー向け",
};
